import type { ReactNode } from 'react';

import { Box, chakra, HStack, Icon, IconButton, Text, VisuallyHidden } from '@chakra-ui/react';
import { Close } from '@m/icons';
import { Drawer } from 'vaul';

import { typographyMixins } from './typography';

export type BottomSheetSize = 'default' | 'full';

export interface BottomSheetProps {
  children?: ReactNode;
  dismissible?: boolean;
  /** Removes padding from the bottom sheet. */
  flush?: boolean;
  footer?: ReactNode;
  header?: ReactNode;
  modal?: boolean;
  onDismiss?: () => void;
  onOpenChange?: (open: boolean) => void;
  open: boolean;
  showCloseButton?: boolean;
  showHandle?: boolean;
  /** `full` fills the viewport height (100dvh). `default` sizes to content with a max of one viewport. */
  size?: BottomSheetSize;
  subtitle?: ReactNode;
  title?: ReactNode;
}

const DrawerOverlay = chakra(Drawer.Overlay, {
  base: {
    bg: 'blackAlpha.600',
    inset: 0,
    position: 'fixed',
    // zIndex needs to match Drawer Content for nested modals to work
    zIndex: 'modal',
  },
});

const DrawerContent = chakra(Drawer.Content, {
  base: {
    bg: 'fill.background.default',
    borderTopRadius: '2xl',
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    left: 0,
    minH: 0,
    outline: 'none',
    overflow: 'hidden',
    position: 'fixed',
    right: 0,
    width: '100%',
    zIndex: 'modal',
  },
});

const DrawerHandle = chakra(Drawer.Handle, {
  base: {
    h: '1',
    w: '12',
  },
});

const DrawerTitle = chakra(Drawer.Title, {
  base: {
    ...typographyMixins.Subheader,
    color: 'text.primary',
    fontWeight: '600',
    m: 0,
  },
});

export const BottomSheet = ({
  children,
  dismissible = true,
  footer,
  header,
  modal,
  onDismiss,
  onOpenChange,
  open,
  showCloseButton = false,
  showHandle = false,
  size = 'default',
  flush = false,
  subtitle,
  title,
}: BottomSheetProps) => {
  const isFullHeight = size === 'full';
  const padding = flush ? 0 : 4;
  const hasTextHeader = Boolean(title || subtitle);
  const shouldCenterTextHeader = isFullHeight && Boolean(title) && !subtitle;
  const shouldBalanceCenteredHeader = shouldCenterTextHeader && showCloseButton && !header;

  return (
    <Drawer.Root
      open={open}
      onOpenChange={(nextOpen) => {
        onOpenChange?.(nextOpen);
        if (!nextOpen) {
          onDismiss?.();
        }
      }}
      modal={modal}
      dismissible={dismissible}
      direction="bottom"
    >
      <Drawer.Portal>
        <DrawerOverlay />
        <DrawerContent
          h={isFullHeight ? '100dvh' : undefined}
          maxH={isFullHeight ? '100dvh' : '100vh'}
        >
          {/* Include a drawer description to make the bottom sheet more accessible. */}
          <VisuallyHidden asChild>
            <Drawer.Description>Bottom sheet content</Drawer.Description>
          </VisuallyHidden>
          {showHandle && (
            <Box display="flex" justifyContent="center" mt={4} mb={flush ? 3 : 0}>
              <DrawerHandle />
            </Box>
          )}
          <Box p={padding} gap={2} display="flex" flexDirection="column" flex="1" minH={0}>
            {(header || hasTextHeader || showCloseButton) && (
              <HStack align="flex-start" justify="space-between" gap={2}>
                {shouldBalanceCenteredHeader && (
                  <Box aria-hidden="true" flexShrink={0} width="48px" height="32px" />
                )}
                <Box flex={1} minW={0} textAlign={shouldCenterTextHeader ? 'center' : 'left'}>
                  {header}
                  {!header && hasTextHeader && (
                    <Box display="flex" flexDirection="column" gap="1">
                      {title && <DrawerTitle>{title}</DrawerTitle>}
                      {subtitle && (
                        <Text variant="SmallBodyRegular" color="text.secondary">
                          {subtitle}
                        </Text>
                      )}
                    </Box>
                  )}
                </Box>
                {showCloseButton && (
                  <IconButton
                    flexShrink={0}
                    width="32px"
                    height="32px"
                    size="xl"
                    variant="ghost"
                    onClick={onDismiss}
                    aria-label="Close"
                  >
                    <Icon as={Close} width="32px" height="32px" />
                  </IconButton>
                )}
              </HStack>
            )}
            <Box overflowY="auto" flex="1" minH={0}>
              {children}
            </Box>
            {footer && <Box>{footer}</Box>}
          </Box>
        </DrawerContent>
      </Drawer.Portal>
    </Drawer.Root>
  );
};

export default BottomSheet;
